import { FC, useEffect, useId, useRef } from 'react';
import { hubspotForms, HUBSPOT_PORTAL_ID, HUBSPOT_SCRIPT_URL, HUBSPOT_REGION } from './constants';

declare global {
  interface Window {
    hbspt?: {
      forms: {
        create: (options: {
          region: string;
          portalId: string;
          formId: string;
          target: string;
        }) => void;
      };
    };
  }
}

interface Props {
  form: keyof typeof hubspotForms;
  className?: string;
  containerId?: string;
}

export const FormContent: FC<Props> = ({ form, className, containerId }) => {
  const instanceId = useId().replace(/:/g, '');
  const formContainerId = containerId ?? `hs-form-container-${form}-${instanceId}`;
  const formContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const formId = hubspotForms[form];
    if (!formId) return;

    const formContainer = formContainerRef.current;
    if (!formContainer) return;

    const alreadyHasForm =
      formContainer.querySelector('.hs-form') || formContainer.querySelector('iframe');
    if (alreadyHasForm) return;

    formContainer.innerHTML = '';

    const createForm = () => {
      if (!window.hbspt) return;
      if (
        formContainerRef.current?.querySelector('.hs-form') ||
        formContainerRef.current?.querySelector('iframe')
      ) {
        return;
      }
      window.hbspt.forms.create({
        region: HUBSPOT_REGION,
        portalId: HUBSPOT_PORTAL_ID,
        formId,
        target: `#${formContainerId}`,
      });
    };

    if (window.hbspt) {
      createForm();
    } else {
      const existingScript = document.querySelector(
        `script[src="${HUBSPOT_SCRIPT_URL}"], script[src="https:${HUBSPOT_SCRIPT_URL}"]`
      );
      if (existingScript) {
        if (window.hbspt) createForm();
        else {
          existingScript.addEventListener('load', createForm);
          return () => {
            existingScript.removeEventListener('load', createForm);
            formContainer.innerHTML = '';
          };
        }
      } else {
        const script = document.createElement('script');
        script.src = HUBSPOT_SCRIPT_URL;
        script.async = true;
        script.addEventListener('load', createForm);
        document.body.appendChild(script);
        return () => {
          script.removeEventListener('load', createForm);
          formContainer.innerHTML = '';
        };
      }
    }

    return () => {
      formContainer.innerHTML = '';
    };
  }, [form, formContainerId]);

  return (
    <div
      id={formContainerId}
      ref={formContainerRef}
      className={className ?? 'hubspot-form-container'}
    />
  );
};
